export const CLIENT_PRICING_TIER_OPTIONS = [
  { value: "retail", label: "Retail" },
  { value: "contractor", label: "Contractor" },
  { value: "dealer", label: "Dealer" },
  { value: "distributor", label: "Distributor" },
];

export const PRICING_TEMPLATE_FIELDS = [
  {
    key: "overhead_ratio",
    label: "Overhead Ratio (%)",
    hint: "Applied to all job costs, including install/delivery pass-through.",
    step: "0.5",
    min: 0,
    max: 100,
  },
  {
    key: "profit_margin",
    label: "Profit Margin (%)",
    hint: "Share of the final price taken as profit (margin on price).",
    step: "0.5",
    min: 0,
    max: 94.9,
  },
  {
    key: "material_sales_tax_rate",
    label: "Material Sales Tax (%)",
    hint: "Applied to material costs only.",
    step: "0.5",
    min: 0,
    max: 100,
  },
  {
    key: "commission_rate",
    label: "Sales Commission (%)",
    hint: "Applied to the final quoted price.",
    step: "0.5",
    min: 0,
    max: 100,
  },
  {
    key: "shop_commission_profit_sharing_rate",
    label: "Shop Commission Profit Sharing (%)",
    hint: "Added on top of sales commission.",
    step: "0.5",
    min: 0,
    max: 100,
  },
];

export const PRICING_TEMPLATE_FIELD_KEYS = PRICING_TEMPLATE_FIELDS.map(
  ({ key }) => key,
);

const DEFAULT_TEMPLATE_VALUES = {
  overhead_ratio: 44.5,
  profit_margin: 17,
  material_sales_tax_rate: 7,
  commission_rate: 6.5,
  shop_commission_profit_sharing_rate: 0,
};

const isFiniteNumber = (value) =>
  typeof value === "number" && Number.isFinite(value);

const toNumberOrDefault = (value, fallback) => {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

export const getPricingTierLabel = (tier) =>
  CLIENT_PRICING_TIER_OPTIONS.find((option) => option.value === tier)?.label ||
  "Retail";

export const extractPricingTemplateValues = (source = {}) =>
  PRICING_TEMPLATE_FIELD_KEYS.reduce((accumulator, field) => {
    accumulator[field] = toNumberOrDefault(
      source?.[field],
      DEFAULT_TEMPLATE_VALUES[field],
    );
    return accumulator;
  }, {});

export const buildDefaultPricingTemplates = (seedValues = {}) => {
  const baseTemplate = extractPricingTemplateValues(seedValues);
  return CLIENT_PRICING_TIER_OPTIONS.reduce((accumulator, { value }) => {
    accumulator[value] = { ...baseTemplate };
    return accumulator;
  }, {});
};

export const normalizePricingTemplates = (templates = {}, seedValues = {}) => {
  const baseTemplate = extractPricingTemplateValues(seedValues);
  return CLIENT_PRICING_TIER_OPTIONS.reduce((accumulator, { value }) => {
    const incomingTemplate = templates?.[value] || {};
    accumulator[value] = PRICING_TEMPLATE_FIELD_KEYS.reduce(
      (normalizedTemplate, field) => {
        normalizedTemplate[field] = toNumberOrDefault(
          incomingTemplate?.[field],
          baseTemplate[field],
        );
        return normalizedTemplate;
      },
      {},
    );
    return accumulator;
  }, {});
};

export const getPricingTemplateValues = (
  tier,
  templates = {},
  fallbackValues = {},
) => {
  const normalizedTemplates = normalizePricingTemplates(
    templates,
    fallbackValues,
  );
  return (
    normalizedTemplates?.[tier] ||
    normalizedTemplates.retail ||
    extractPricingTemplateValues(fallbackValues)
  );
};

export const syncDefaultPricingFields = (settings = {}) => {
  const defaultTier = CLIENT_PRICING_TIER_OPTIONS.some(
    ({ value }) => value === settings.default_client_pricing_tier,
  )
    ? settings.default_client_pricing_tier
    : "retail";
  const pricing_templates = normalizePricingTemplates(
    settings.pricing_templates,
    settings,
  );
  const defaultTemplate = getPricingTemplateValues(
    defaultTier,
    pricing_templates,
    settings,
  );

  return {
    ...settings,
    default_client_pricing_tier: defaultTier,
    pricing_templates,
    ...defaultTemplate,
  };
};

export const matchesTemplateValue = (currentValue, templateValue) => {
  if (
    currentValue === undefined ||
    currentValue === null ||
    currentValue === ""
  ) {
    return true;
  }
  if (isFiniteNumber(currentValue)) {
    return currentValue === templateValue;
  }
  const parsed = Number.parseFloat(currentValue);
  return Number.isFinite(parsed) ? parsed === templateValue : false;
};
